import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Textarea } from '@/app/components/ui/textarea';
import { useCreateAuditPrep } from '@/hooks/use-audit-prep';
import { useComplianceFrameworks } from '@/hooks/use-compliance';
import type { AuditType } from '@/lib/types';
import { AUDIT_TYPE_OPTIONS } from '@/lib/audit-types';

const AUDIT_TYPES = AUDIT_TYPE_OPTIONS;

export function AuditPrepNewPage() {
  const navigate = useNavigate();
  const createMutation = useCreateAuditPrep();
  const { data: frameworks } = useComplianceFrameworks();

  const [auditType, setAuditType] = useState<AuditType>('stage_1');
  const [frameworkId, setFrameworkId] = useState<string>('');
  const [auditDate, setAuditDate] = useState('');
  const [auditorName, setAuditorName] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!frameworkId) return;

    createMutation.mutate(
      {
        framework: Number(frameworkId),
        audit_type: auditType,
        audit_date: auditDate || null,
        auditor_name: auditorName,
        notes,
      },
      {
        onSuccess: (data) => {
          navigate(`/compliance/audit-prep/${data.id}`);
        },
      }
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Audit Preparation</h1>
          <p className="text-gray-600 mt-1">
            Set up a new IRATA audit preparation session
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Audit Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="framework">Compliance Framework *</Label>
              <Select value={frameworkId} onValueChange={setFrameworkId}>
                <SelectTrigger id="framework">
                  <SelectValue placeholder="Select framework..." />
                </SelectTrigger>
                <SelectContent>
                  {frameworks?.map((fw) => (
                    <SelectItem key={fw.id} value={String(fw.id)}>
                      {fw.framework_type_display} — {fw.membership_number || 'No membership #'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="auditType">Audit Type *</Label>
              <Select
                value={auditType}
                onValueChange={(v) => setAuditType(v as AuditType)}
              >
                <SelectTrigger id="auditType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="auditDate">Target Audit Date</Label>
              <Input
                id="auditDate"
                type="date"
                value={auditDate}
                onChange={(e) => setAuditDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="auditorName">Auditor Name</Label>
              <Input
                id="auditorName"
                value={auditorName}
                onChange={(e) => setAuditorName(e.target.value)}
                placeholder="Name of the assigned auditor"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes about this audit preparation"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/compliance/audit-prep')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Audit Prep'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
